import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';

/**
 * Accept team-switch invite (signed-in user moves to inviter&apos;s team).
 */
export const TeamSwitchInvitePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const selfServeSignup = useFeatureFlag('SelfServiceSignup');

    const token = (searchParams.get('token') || '').trim();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        document.title = 'Team invitation | Calculist';
    }, []);

    if (!selfServeSignup) {
        return <Navigate to="/auth/login" replace />;
    }

    const handleAccept = async () => {
        setError(null);
        setSubmitting(true);
        try {
            await authApiService.signupAcceptTeamSwitchInvite(token);
            setDone(true);
        } catch (err: unknown) {
            debugError('signupAcceptTeamSwitchInvite', err);
            setError(err instanceof Error ? err.message : 'Could not accept invitation.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={themedStyles.card.default}>
                    <p>Invalid invitation link.</p>
                    <Link to="/" style={{ color: theme.colors.primary }}>Home</Link>
                </div>
            </div>
        );
    }

    if (done) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.background,
                padding: theme.spacing.md,
            }}>
                <div style={themedStyles.card.default}>
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0 }}>Team updated</h1>
                    <p style={themedStyles.text.body}>You have switched to the invited team.</p>
                    <Link to="/cases/list" style={{ color: theme.colors.primary }}>Go to cases</Link>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            <div style={themedStyles.card.default}>
                <h1 style={{ ...themedStyles.text.heading, marginTop: 0 }}>Switch teams</h1>
                <p style={themedStyles.text.body}>
                    You must be signed in as the invited user. If you are not signed in, open this link again after signing in.
                </p>
                {error && (
                    <div style={themedStyles.alert.error}>
                        <span style={{ fontSize: '20px' }}>⚠️</span>
                        <div style={{ flex: 1 }}>{error}</div>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => void handleAccept()}
                    disabled={submitting}
                    style={{
                        ...themedStyles.button.primary,
                        ...(submitting ? themedStyles.button.disabled : {}),
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    }}
                >
                    {submitting ? 'Working…' : 'Accept invitation'}
                </button>
            </div>
        </div>
    );
};
